import type { LoadPlanningCurveDataPointDTO } from '@/src/api/types/procurement'
import { FlexRow, GraphLegends } from '@/src/components'
import { BaseText } from '@/src/components/BaseText'
import { GraphTick2 } from '@/src/components/GraphTick2'
import { ResetZoomButton } from '@/src/components/charts/ResetZoomButton'
import { useChartZoom } from '@/src/components/charts/useChartZoom.hook'
import { ALL_LOCATIONS } from '@/src/contexts/LocationsContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { useXAxisProps } from '@/src/features/analytics/useXAxisProps.hook'
import { useLoadPlanningCurve } from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import {
  extractValueForBar,
  getColor,
  getDataByGranularity,
  getDateFormatByGranularity,
  getEnergyUnitType,
  getLoadPlanningLegend,
  getPeakPointValue,
  getPropertyForLeftBar,
  getPropertyForRightBar,
  mapLoadPlanningUnitText,
  zoomDataPointsTimeRange,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { LoadPlanningGraphTooltip } from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanningGraphTooltip'
import { appColors } from '@/src/theme'
import { daysBetweenDates } from '@/src/utils/dateUtil'
import { UNIT_SCALE_RANGE, scaleUnitToTargetValue } from '@/src/utils/measurementUnitUtils'
import { Box, Flex, Spinner } from '@chakra-ui/react'
import { mapToEnergyTypeDto } from '@features/procurement-and-markets/details/types/adjustment.utils'
import { formatNumberCompact } from '@shared/utils'
import { format } from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'
import { getNiceTickValues } from 'recharts-scale'

interface LoadPlanningGraphProps {
  companyId: number
  locationId?: number
  forecastYear: number
  energyType: 'ELECTRICITY' | 'GAS'
  granularity: GranularityEnum
  dateRange?: { from: Date; to: Date }
}

export const LoadPlanningGraph = ({ companyId, locationId, granularity, forecastYear, energyType, dateRange }: LoadPlanningGraphProps) => {
  const { t } = useTranslation()
  const normalizedProps = {
    companyId,
    locationId: typeof locationId === 'number' && locationId !== ALL_LOCATIONS ? Number(locationId) : undefined,
    year: forecastYear,
    energyType: mapToEnergyTypeDto(energyType),
    granularity,
  }

  const { data: loadForecastData, isFetching: isForecastLoading } = useLoadPlanningCurve(normalizedProps)
  const fullDataSource = loadForecastData ? getDataByGranularity(loadForecastData, granularity) : []
  const dataSource = zoomDataPointsTimeRange(fullDataSource, dateRange)

  const dataWithTimestamp = useMemo(() => dataSource.map(d => ({ ...d, timestamp: d.timestampStart })), [dataSource])

  const { zoomedData, isZoomed, selectionStart, selectionEnd, isSelecting, handleMouseDown, handleMouseMove, handleMouseUp, resetZoom } =
    useChartZoom({ data: dataWithTimestamp })

  const chartMouseHandlers = {
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseUp,
  }

  const isDateRangeMoreThanTwoDays = dateRange ? daysBetweenDates(dateRange.from, dateRange.to) > 2 : false

  const {
    ref: graphContainerRef,
    tickFormatProps,
    xAxisInterval,
  } = useXAxisProps({
    dataPoints: zoomedData ?? [],
    datePropertyName: 'timestampStart',
    granularity: isDateRangeMoreThanTwoDays ? GranularityEnum.ONE_DAY : granularity,
  })

  if (!loadForecastData) {
    return <Spinner />
  }

  const maxValue = getPeakPointValue(zoomedData, granularity)
  const niceTickValues = getNiceTickValues([0, maxValue], 5, true)
  const legends = getLoadPlanningLegend(zoomedData, t)

  const unitProperty = getEnergyUnitType(granularity)
  const unit = mapLoadPlanningUnitText(zoomedData[0]?.savedValue?.[unitProperty]?.unit) || 'kWh'
  const { targetUnit, scaler } = scaleUnitToTargetValue(maxValue, unit, UNIT_SCALE_RANGE)

  return (
    <Box position='relative'>
      <FlexRow justifyContent={'space-between'} width={'100%'}>
        <Flex>
          <BaseText color='gray.400' fontSize={14} marginBottom={2}>
            {targetUnit}
          </BaseText>
        </Flex>
        <Flex gap={4} alignItems='center' h={'12px'}>
          <ResetZoomButton onReset={resetZoom} isVisible={isZoomed} />
          <GraphLegends legends={legends} />
        </Flex>
      </FlexRow>
      <ResponsiveContainer width={'100%'} height={420} ref={graphContainerRef}>
        <ComposedChart data={zoomedData} {...chartMouseHandlers} style={{ cursor: 'crosshair' }}>
          <XAxis
            dataKey={'timestampStart'}
            tickLine={false}
            tick={props => (
              <GraphTick2
                tickProps={props}
                formatProps={{ ...tickFormatProps, showYear: granularity !== GranularityEnum.ONE_MONTH }}
                customValueFormatter={v => format(new Date(v), getDateFormatByGranularity(granularity))}
              />
            )}
            tickMargin={8}
            axisLine={{ stroke: appColors.gray[100] }}
            interval={granularity === GranularityEnum.ONE_MONTH ? 0 : xAxisInterval}
          />
          <YAxis
            type='number'
            domain={[0, niceTickValues.at(-1) || 0]}
            ticks={niceTickValues}
            tickLine={false}
            axisLine={false}
            tickMargin={0}
            tick={{
              fill: appColors.gray['400'],
            }}
            tickFormatter={value => formatNumberCompact(value * scaler, { maximumFractionDigits: 1 })}
            label={{
              value: unit,
              position: 'top',
              style: { fill: appColors.gray['600'] },
            }}
          />
          <CartesianGrid vertical={false} stroke={'#F3F3F3'} />
          {getChartComponents(granularity)}
          <Tooltip
            cursor={!isSelecting}
            content={(props: TooltipProps<number, string>) => <LoadPlanningGraphTooltip {...props} granularity={granularity} />}
            wrapperStyle={{ outline: 'none' }}
          />
          {isSelecting && selectionStart && selectionEnd && (
            <ReferenceArea
              x1={selectionStart}
              x2={selectionEnd}
              fill={appColors.blue['500']}
              fillOpacity={0.2}
              stroke={appColors.blue['500']}
              strokeOpacity={0.5}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {isForecastLoading && (
        <Flex position='absolute' inset={0} bg='whiteAlpha.700' align='center' justify='center'>
          <Spinner />
        </Flex>
      )}
    </Box>
  )
}

const getChartComponents = (granularity: GranularityEnum) => {
  const unitProperty = getEnergyUnitType(granularity)
  if (granularity === GranularityEnum.FIFTEEN_MINUTES || granularity === GranularityEnum.ONE_HOUR) {
    return (
      <>
        <Line
          dataKey={`draftValue.${unitProperty}.amount`}
          type={'monotone'}
          strokeWidth={1}
          dot={false}
          stroke={getColor('draftValue')}
          isAnimationActive={false}
        />
        <Line
          dataKey={`savedValue.${unitProperty}.amount`}
          type={'monotone'}
          strokeWidth={1}
          dot={false}
          stroke={getColor('savedValue')}
          isAnimationActive={false}
        />
        <Line
          dataKey={`actualConsumptionValue.${unitProperty}.amount`}
          type={'monotone'}
          strokeWidth={1}
          dot={false}
          stroke={getColor('actualConsumptionValue')}
          isAnimationActive={false}
        />
      </>
    )
  }
  return (
    <>
      <Bar
        radius={[4, 4, 0, 0]}
        barSize={11}
        dataKey={(p: LoadPlanningCurveDataPointDTO) => {
          if (!p) return null
          return extractValueForBar('left', p, granularity)
        }}
        shape={props => <Rectangle {...props} fill={getColor(getPropertyForLeftBar(props, granularity))} />}
        isAnimationActive={false}
      />
      <Bar
        radius={[4, 4, 0, 0]}
        barSize={11}
        dataKey={p => {
          if (!p) return null
          return extractValueForBar('right', p, granularity)
        }}
        shape={props => <Rectangle {...props} fill={getColor(getPropertyForRightBar(props, granularity))} />}
        isAnimationActive={false}
      />
    </>
  )
}
